import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ChefHat, Utensils, Coffee, Star } from 'lucide-react';

interface MealFeedCardProps {
  mealType: string;
  recipeTitle: string;
  tags: string[];
  dayOfWeek?: string;
  averageRating?: number | null;
}

const getMealTypeConfig = (mealType: string) => {
  switch (mealType.toLowerCase()) {
    case 'breakfast':
      return { 
        label: 'Breakfast', 
        bgColor: 'bg-amber-50', 
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        labelBg: 'bg-white',
        labelColor: 'text-amber-700',
        Icon: Coffee
      };
    case 'lunch':
      return { 
        label: 'Lunch', 
        bgColor: 'bg-emerald-50', 
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        labelBg: 'bg-white',
        labelColor: 'text-emerald-700',
        Icon: Utensils
      };
    case 'dinner':
    default:
      return { 
        label: 'Dinner', 
        bgColor: 'bg-violet-100', 
        iconBg: 'bg-violet-200',
        iconColor: 'text-violet-600',
        labelBg: 'bg-white',
        labelColor: 'text-violet-700',
        Icon: ChefHat
      };
  }
};

export const MealFeedCard: React.FC<MealFeedCardProps> = ({
  mealType,
  recipeTitle,
  tags,
  dayOfWeek,
  averageRating,
}) => {
  const { label, bgColor, iconBg, iconColor, labelBg, labelColor, Icon } = getMealTypeConfig(mealType);

  // Render star rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star key={i} className="w-3.5 h-3.5 fill-amber-400/50 text-amber-400" />
        );
      } else {
        stars.push(
          <Star key={i} className="w-3.5 h-3.5 text-gray-300" />
        );
      }
    }
    return stars;
  };

  return (
    <div 
      className={`
        relative overflow-hidden rounded-2xl p-5 transition-all duration-300
        hover:shadow-lg hover:scale-[1.02] ${bgColor}
      `}
    >
      {/* Header with icon and meal type */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${labelBg} ${labelColor}`}>
          {label}
        </span>
      </div>
      
      {/* Recipe title */}
      <h3 className="font-semibold text-foreground line-clamp-2 leading-tight mb-3 text-lg">
        {recipeTitle}
      </h3>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tags.slice(0, 3).map((tag, index) => (
            <Badge 
              key={index} 
              variant="secondary" 
              className="text-xs bg-white/80 text-foreground border border-border/30 hover:bg-white"
            >
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Star rating */}
      {averageRating && (
        <div className="flex items-center gap-0.5 mb-3">
          {renderStars(averageRating)}
        </div>
      )}

      {/* Planned for day */}
      {dayOfWeek && (
        <p className="text-sm text-muted-foreground">
          Planned for {dayOfWeek}
        </p>
      )}
    </div>
  );
};

export default MealFeedCard;